import { Injectable } from '@angular/core';
import { HttpAuth } from '@core/services/http-auth';
import { environment } from '@src/environments/environment';
import { IsAllowedResponse, VerificationStatus } from '@core/models';
import { Store } from '@ngrx/store';
import { map, of, switchMap } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import { userFeature } from '@state';
import { VerificationCodeModalComponent } from '@shared/components';
import { ModalService } from '@core/services/modal.service';
import { catchError } from 'rxjs/operators';
import { VerificationType } from '@core/enums';

export interface VerificationState {
  isVerificationNeeded: boolean;
  isVerified: boolean;
  code?: number;
  isCodeInvalid?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class VerificationService {
  constructor(
    private readonly modalService: ModalService,
    private readonly httpAuth: HttpAuth,
    private readonly store: Store
  ) {}

  public performActionWithVerification(type: VerificationType) {
    if (environment.settings.skipSmsVerification && type === VerificationType.Sms) {
      return of({
        isVerificationNeeded: false,
        isVerified: true,
      } as VerificationState);
    }
    if (environment.settings.skipEmailVerification && type === VerificationType.Email) {
      return of({
        isVerificationNeeded: false,
        isVerified: true,
      } as VerificationState);
    }

    return this.checkIfVerificationNeeded().pipe(
      concatLatestFrom(() => this.store.select(userFeature.selectSelectedMsisdnNumber)),
      switchMap(([isVerificationNeeded, msisdn]) => {
        if (isVerificationNeeded) {
          // sending verification code if needed
          return this.modalService
            .open(VerificationCodeModalComponent, {
              title: 'Verification code',
              verificationType: type,
            })
            .pipe(
              map((isVerified: boolean) => ({
                isVerificationNeeded,
                isVerified: !!isVerified,
              }))
            );
        }
        // skipping verification sending
        return of({
          isVerificationNeeded,
          isVerified: true,
        } as VerificationState);
      })
    );
  }

  public checkIfVerificationNeeded() {
    return this.httpAuth
      .get<IsAllowedResponse>(`${environment.apiUrl}/api/users/me/IsEditAllow`)
      .pipe(
        map((content) => content.status === VerificationStatus.Error),
        catchError(() => of(true))
      );
  }

  public sendVerificationCode(msisdn: string, verificationType: VerificationType) {
    return this.httpAuth.post(
      `${environment.apiUrl}/api/msisdns/${msisdn}/send-code?transport=${verificationType}`
    );
  }

  public verifySmsCode(code: string) {
    return this.httpAuth
      .post<IsAllowedResponse>(`${environment.apiUrl}/api/users/me/verify-sms-code`, {
        code,
      })
      .pipe(
        map((response) => response.status === VerificationStatus.Ok),
        catchError(() => of(false))
      );
  }
}
